import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Send, MapPin, Camera, Gift, Instagram, Calendar, Search, Bell, ThumbsUp, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

const huntSteps = [
  {
    icon: Search,
    title: "Follow the Clue",
    desc: "Each month I'll post a riddle on Instagram. The clue will lead you to a hidden token somewhere local — near town, a shop, on the beach or nearby.",
  },
  {
    icon: MapPin,
    title: "Find the Token",
    desc: "Head out and hunt! The token could be tucked away anywhere in and around Bude. First to find it wins.",
  },
  {
    icon: Camera,
    title: "Post and Tag",
    desc: "Snap a photo with your find and post it on socials. Tag @justjjax so everyone can celebrate with you.",
  },
  {
    icon: Gift,
    title: "Claim Your Prize",
    desc: "Prizes change every month — from hair products and a free haircut to goodies from participating local shops.",
  },
];

export default function CommunityPage() {
  const [suggestion, setSuggestion] = useState("");
  const [suggName, setSuggName] = useState("");
  const [suggEmail, setSuggEmail] = useState("");
  const [suggCategory, setSuggCategory] = useState("Event");
  const [suggSent, setSuggSent] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [hostName, setHostName] = useState("");
  const [hostEmail, setHostEmail] = useState("");
  const [hostMessage, setHostMessage] = useState("");
  const [hostSent, setHostSent] = useState(false);
  const [votedIds, setVotedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("justjjax_voted") || "[]"); }
    catch { return []; }
  });

  const loadIdeas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/suggestions/public`);
      setIdeas(data);
    } catch {
      setIdeas([]);
    }
  }, []);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const handleVote = async (id) => {
    if (votedIds.includes(id)) return;
    try {
      const { data } = await axios.post(`${API}/community/suggestions/${id}/vote`);
      setIdeas((prev) =>
        [...prev.map((it) => (it.id === id ? { ...it, votes: data.votes } : it))]
          .sort((a, b) => (b.votes || 0) - (a.votes || 0))
      );
      const next = [...votedIds, id];
      setVotedIds(next);
      localStorage.setItem("justjjax_voted", JSON.stringify(next));
      toast.success("Thanks for voting!");
    } catch {
      toast.error("Couldn't register your vote, please try again");
    }
  };

  const handleSuggestion = async (e) => {    e.preventDefault();
    if (!suggestion.trim()) {
      toast.error("Please enter your suggestion");
      return;
    }
    try {
      await axios.post(`${API}/community/suggestions`, {
        name: suggName,
        email: suggEmail,
        category: suggCategory,
        suggestion: suggestion,
      });
      toast.success("Thanks for your suggestion!");
      setSuggSent(true);
      setSuggestion("");
      setSuggName("");
      setSuggEmail("");
      setSuggCategory("Event");
      loadIdeas();
    } catch {
      toast.error("Something went wrong, please try again");
    }
  };

  const handleHostOffer = async (e) => {
    e.preventDefault();
    if (!hostMessage.trim()) {
      toast.error("Please tell me about your talk or event");
      return;
    }
    try {
      await axios.post(`${API}/community/host-offers`, {
        name: hostName,
        email: hostEmail,
        message: hostMessage,
      });
      toast.success("Thank you! I'll be in touch.");
      setHostSent(true);
      setHostName("");
      setHostEmail("");
      setHostMessage("");
    } catch {
      toast.error("Something went wrong, please try again");
    }
  };

  return (
    <div className="pt-28 lg:pt-44" data-testid="community-page">
      {/* Hero with brand color */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-sunset/20 via-peachlight to-sage/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-accent text-2xl text-sunset mb-1">come together</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3">
            Community & Events
          </h1>
          <p className="text-charcoal/60 max-w-xl mx-auto leading-relaxed">
            Events, workshops and gatherings designed to bring people together, support local and
            share good energy. All in beautiful Cornwall.
          </p>
        </div>
      </section>

      {/* Suggest a Course */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-sage/15 to-peachlight" data-testid="suggestion-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-3xl p-8 md:p-10 border-2 border-sage/15 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-sunset/10 rounded-xl flex items-center justify-center">
                <Lightbulb className="text-sunset" size={20} />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight">
                Suggest an Event, Talk or Lesson
              </h2>
            </div>
            <p className="text-charcoal/60 text-sm leading-relaxed mb-6">
              Got an idea for an event, talk, workshop or lesson you'd love to see at Justjjax? Tell me
              about it! I'm always looking for fresh ideas from the community — the things you suggest
              are the things I'll try to make happen.
            </p>
            {!suggSent ? (
              <form onSubmit={handleSuggestion} className="space-y-4">
                <input
                  type="text"
                  value={suggName}
                  onChange={(e) => setSuggName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-sunset/30"
                  data-testid="suggestion-name"
                />
                <input
                  type="email"
                  value={suggEmail}
                  onChange={(e) => setSuggEmail(e.target.value)}
                  placeholder="Your email (optional — so I can reply if it happens)"
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-sunset/30"
                  data-testid="suggestion-email"
                />
                <div>
                  <label className="block text-xs font-medium text-charcoal/60 mb-2">What would you like to see?</label>
                  <div className="flex flex-wrap gap-2" data-testid="suggestion-category-group">
                    {["Event", "Talk", "Lesson", "Workshop", "Other"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setSuggCategory(c)}
                        className={`rounded-full px-4 py-2 text-xs font-medium transition-all border ${
                          suggCategory === c
                            ? "bg-olive text-white border-olive"
                            : "bg-white text-charcoal/60 border-ocean/10 hover:text-black"
                        }`}
                        data-testid={`suggestion-category-${c.toLowerCase()}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder="Tell me about the event, talk or lesson you'd love to attend..."
                  rows={3}
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-sunset/30 resize-none"
                  data-testid="suggestion-text"
                />
                <button
                  type="submit"
                  className="bg-olive text-white hover:bg-eucalyptus rounded-full px-8 py-3 text-sm font-medium transition-all duration-300 flex items-center gap-2"
                  data-testid="suggestion-submit"
                >
                  <Send size={14} /> Send Suggestion
                </button>
              </form>
            ) : (
              <div className="bg-sage/10 rounded-2xl p-5" data-testid="suggestion-success">
                <p className="font-medium text-sage">Thanks for your suggestion!</p>
                <p className="text-sm text-charcoal/60 mt-1">I'll review it and hopefully make it happen.</p>
                <button
                  onClick={() => setSuggSent(false)}
                  className="mt-3 text-xs text-sunset font-medium underline"
                >
                  Submit another idea
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Offer to host a talk or event */}
      <section className="py-10 md:py-14 bg-white/40" data-testid="host-offer-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-br from-olive/5 to-sage/10 rounded-3xl p-8 md:p-10 border border-olive/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-olive/10 rounded-xl flex items-center justify-center">
                <Calendar className="text-olive" size={20} />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight">
                Fancy Hosting a Talk or Event?
              </h2>
            </div>
            <p className="text-charcoal/60 text-sm leading-relaxed mb-6">
              Got something you'd love to share — a talk, workshop or hands-on session? If you'd like to
              host an event with Justjjax, tell me a little about it and I'll be in touch.
            </p>
            {!hostSent ? (
              <form onSubmit={handleHostOffer} className="space-y-4">
                <input
                  type="text"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
                  data-testid="host-name"
                />
                <input
                  type="email"
                  value={hostEmail}
                  onChange={(e) => setHostEmail(e.target.value)}
                  placeholder="Your email (so I can get back to you)"
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
                  data-testid="host-email"
                />
                <textarea
                  value={hostMessage}
                  onChange={(e) => setHostMessage(e.target.value)}
                  placeholder="What would you like to host? Tell me about your talk, workshop or event..."
                  rows={3}
                  className="w-full px-5 py-3 rounded-xl border border-ocean/10 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30 resize-none"
                  data-testid="host-message"
                />
                <button
                  type="submit"
                  className="bg-olive text-white hover:bg-eucalyptus rounded-full px-8 py-3 text-sm font-medium transition-all duration-300 flex items-center gap-2"
                  data-testid="host-submit"
                >
                  <Send size={14} /> Offer to Host
                </button>
              </form>
            ) : (
              <div className="bg-olive/10 rounded-2xl p-5" data-testid="host-success">
                <p className="font-medium text-olive">Thank you! I'll be in touch.</p>
                <p className="text-sm text-charcoal/60 mt-1">I love having guest hosts — I'll read your note and get back to you.</p>
                <button
                  onClick={() => setHostSent(false)}
                  className="mt-3 text-xs text-sunset font-medium underline"
                >
                  Send another
                </button>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Popular Ideas — vote */}
      {ideas.length > 0 && (
        <section className="py-12 md:py-16" data-testid="popular-ideas-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 bg-sunset/10 text-sunset text-xs font-medium tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">
                <TrendingUp size={13} /> Community Ideas
              </span>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">
                Most-Wanted Ideas
              </h2>
              <p className="text-charcoal/60 max-w-xl mx-auto leading-relaxed">
                Here's what the community would love to see. Vote for the ones you'd attend — the most
                popular ideas are the ones I'll bring to life first.
              </p>
            </div>

            <div className="space-y-3" data-testid="ideas-list">
              {ideas.map((idea, i) => {
                const voted = votedIds.includes(idea.id);
                return (
                  <motion.div
                    key={idea.id}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(i * 0.05, 0.3) }}
                    className="bg-white rounded-2xl border border-black/5 p-4 md:p-5 flex items-center gap-4 hover:shadow-md transition-shadow"
                    data-testid={`idea-card-${i}`}
                  >
                    <button
                      onClick={() => handleVote(idea.id)}
                      disabled={voted}
                      className={`flex flex-col items-center justify-center rounded-xl px-3 py-2 min-w-[56px] transition-all border ${
                        voted
                          ? "bg-olive text-white border-olive cursor-default"
                          : "bg-sage/10 text-olive border-sage/20 hover:bg-olive hover:text-white"
                      }`}
                      data-testid={`idea-vote-btn-${i}`}
                      aria-label="Vote for this idea"
                    >
                      <ThumbsUp size={16} />
                      <span className="text-sm font-semibold mt-0.5" data-testid={`idea-votes-${i}`}>{idea.votes || 0}</span>
                    </button>
                    <div className="flex-1 min-w-0">
                      <span className="inline-block text-[11px] text-olive bg-olive/10 rounded-full px-2.5 py-0.5 font-medium mb-1">
                        {idea.category || "General"}
                      </span>
                      <p className="text-sm text-charcoal/80 leading-relaxed">{idea.suggestion}</p>
                      <p className="text-xs text-charcoal/40 mt-1">— {idea.name || "Anonymous"}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}


      {/* The Good Vibes Hunt */}
      <section className="py-12 md:py-16" data-testid="good-vibes-hunt" id="good-vibes-hunt">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-amber/10 text-amber text-xs font-medium tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">
              <Calendar size={13} /> Coming Soon · Monthly Event
            </span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-3" data-testid="hunt-heading">
              The Good Vibes Hunt
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto leading-relaxed">
              A monthly treasure hunt around Bude that brings the community together, supports local
              shops and spreads good vibes. One hidden token, one riddle, one lucky finder.
            </p>
          </div>

          {/* How it works */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {huntSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bg-white/70 rounded-2xl border border-black/5 p-6 text-center hover:shadow-md transition-shadow"
                data-testid={`hunt-step-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-olive/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon size={22} className="text-olive" />
                </div>
                <h3 className="font-display text-base text-black mb-2">{step.title}</h3>
                <p className="text-xs text-charcoal/60 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Key details card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-olive/5 to-sage/10 rounded-3xl border border-olive/10 p-8 md:p-10"
            data-testid="hunt-details"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-2xl text-black mb-3">How to Play</h3>
                <ul className="space-y-3 text-sm text-charcoal/70 leading-relaxed">
                  <li className="flex gap-3">
                    <Instagram size={16} className="text-olive mt-0.5 flex-shrink-0" />
                    <span>Follow <a href="https://www.instagram.com/justjjax/" target="_blank" rel="noopener noreferrer" className="font-medium text-olive hover:text-amber transition-colors">@justjjax</a> on Instagram for the monthly riddle clue</span>
                  </li>
                  <li className="flex gap-3">
                    <MapPin size={16} className="text-olive mt-0.5 flex-shrink-0" />
                    <span>The token will be hidden somewhere local — near town, on the beach, by a shop or along a walking route</span>
                  </li>
                  <li className="flex gap-3">
                    <Camera size={16} className="text-olive mt-0.5 flex-shrink-0" />
                    <span>When you find it, post a photo on socials and <strong>tag @justjjax</strong> to claim your prize</span>
                  </li>
                  <li className="flex gap-3">
                    <Gift size={16} className="text-olive mt-0.5 flex-shrink-0" />
                    <span>Prizes vary each month — hair products, a free haircut, items from participating local shops and more</span>
                  </li>
                </ul>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-2xl p-6 border border-olive/10 inline-block">
                  <p className="font-accent text-lg text-sunset mb-1">launching soon</p>
                  <p className="font-display text-xl text-black mb-3">Coming Soon</p>
                  <p className="text-xs text-charcoal/50 mb-5 max-w-xs mx-auto">
                    The Good Vibes Hunt is almost here. Follow @justjjax on Instagram to be the first to catch the very first riddle.
                  </p>
                  <a
                    href="https://www.instagram.com/justjjax/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-olive text-white hover:bg-olive/80 rounded-full px-6 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
                    data-testid="hunt-follow-btn"
                  >
                    <Instagram size={16} /> Follow for launch
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events */}
      <section className="py-12 md:py-16 bg-white/40" data-testid="events-section" id="events">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-olive/10 text-olive text-xs font-medium tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">
              <Calendar size={13} /> Events
            </span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">Workshops & Gatherings</h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto leading-relaxed">
              Workshops, pop-ups, community meetups and good-vibe gatherings. Follow along and grab
              your spot when something catches your eye.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-6"
            data-testid="events-list"
          >
            {/* The Good Vibes Hunt — listed as an event */}
            <div className="bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-md transition-shadow flex flex-col" data-testid="event-card-good-vibes-hunt">
              <div className="p-6 flex flex-col flex-1">
                <div className="flex flex-wrap gap-3 mb-3">
                  <span className="inline-flex items-center gap-1 text-xs text-olive bg-olive/10 rounded-full px-3 py-1 font-medium">
                    <Calendar size={12} /> Monthly
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-charcoal/50 bg-sage/10 rounded-full px-3 py-1">
                    <MapPin size={12} /> Bude & around
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-amber bg-amber/10 rounded-full px-3 py-1 font-medium">
                    Coming Soon
                  </span>
                </div>
                <h3 className="font-display text-lg text-black mb-2">The Good Vibes Hunt</h3>
                <p className="text-sm text-charcoal/60 leading-relaxed mb-4 flex-1">
                  A monthly treasure hunt around Bude. Follow the riddle clue on Instagram, find the
                  hidden token and win a prize — from hair products to a free haircut or local-shop
                  goodies.
                </p>
                <a
                  href="#good-vibes-hunt"
                  className="bg-olive text-white hover:bg-olive/80 rounded-full px-5 py-2 text-xs font-medium transition-all inline-flex items-center gap-2 self-start"
                  data-testid="event-hunt-details-btn"
                >
                  <Search size={13} /> See how it works
                </a>
              </div>
            </div>

            {/* More events coming soon */}
            <div className="bg-white/60 rounded-2xl border border-dashed border-black/10 flex flex-col items-center justify-center text-center p-8" data-testid="events-coming-soon">
              <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mb-4">
                <Bell size={26} className="text-sage" />
              </div>
              <h3 className="font-display text-lg text-black mb-2">More Events Coming Soon</h3>
              <p className="text-charcoal/60 text-xs max-w-xs mx-auto mb-5">
                Workshops, wellness meetups and pop-ups are in the works. Follow along to be the first
                to know when tickets drop.
              </p>
              <a
                href="https://www.instagram.com/justjjax/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-charcoal text-white hover:bg-black rounded-full px-5 py-2.5 text-xs font-medium transition-all inline-flex items-center gap-2"
                data-testid="events-follow-instagram"
              >
                <Instagram size={14} /> Follow on Instagram
              </a>
            </div>
          </motion.div>

          <div className="mt-8">
            <h3 className="font-display text-2xl text-black mb-6 text-center">What to Expect</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: "Curl Workshops", desc: "Hands-on sessions where you learn to style and care for your natural curls" },
                { title: "Wellness Meetups", desc: "Community gatherings focused on holistic living, mindset and self-care" },
                { title: "Pop-up Events", desc: "Exclusive product launches, styling sessions and collaborations" },
              ].map((item, i) => (
                <div key={i} className="bg-white/70 rounded-2xl p-6 border border-black/5 text-center">
                  <h4 className="font-display text-base text-black mb-2">{item.title}</h4>
                  <p className="text-xs text-charcoal/60">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}
